import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Mixing mixpanel into backbone",
  "description": "",
  "published": "2014-01-24T00:00:00.000Z",
  "redirect_from": ["/blog/mixing-mixpanel-into-backbone/swizec/6498"],
  "categories": "Analytics, backbone, JavaScript, Mixpanel, Programming, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`"Hey, so nobody's looked at our `}<a parentName="em" {...{
          "href": "http://www.mixpanel.com",
          "title": "Mixpanel"
        }}>{`Mixpanel`}</a>{` integration in months and the product has changed a lot, so uh ... can you fix that?"`}</em></p>
    <p>{`Every developer's favourite thing to hear. Logs and metrics going out of sync with the product they're logging and metricsing. Always fun to fix ...`}</p>
    <p>{`How can we avoid ever fixing this again?`}</p>
    <p>{`We'd like an automagic logging or metrics system that fulfills two criteria:`}</p>
    <ul>
      <li parentName="ul">{`self-adapting`}</li>
      <li parentName="ul">{`doesn't show up in the code`}</li>
    </ul>
    <p>{`In my case, I had to fix an old javascript integration of Mixpanel. Our approach was a mix of globally assigning `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Observer_pattern",
        "title": "Observer pattern"
      }}>{`event listeners`}</a>{` and peppering our view/model/whatever code with `}<inlineCode parentName="p">{`mixpanel.track`}</inlineCode>{` calls.`}</p>
    <p>{`The problem is that global event listeners will eventually fall out of sync when a class name changes or a link is removed. Peppering your code with calls to tracking code is even more brittle.`}</p>
    <p>{`Tracking calls are essentially comments on your code. Comments and code `}<em parentName="p">{`always`}</em>{` drift apart. Always. It's just a fact. The same thing happens to tracking calls.`}</p>
    <p>{`Except it's even worse than comments. At least comments are helpful in theory. Tracking code just muddies the logic of your functions. `}<inlineCode parentName="p">{`do_something`}</inlineCode>{` becomes `}<inlineCode parentName="p">{`do_something_and_track_business_event_X`}</inlineCode>{`.`}</p>
    <p>{`Of course you won't name your functions like that because you're not a tool, but you `}<em parentName="p">{`have`}</em>{` introduced an extra side-effect. Nobody likes strange side-effects. Especially the kind that make calls to remote services.`}</p>
    <p>{`Therefore, the only solution is moving tracking code an `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Abstraction_layer",
        "title": "Abstraction layer"
      }}>{`abstraction layer`}</a>{` higher. Instead of relying on everyone in the team to maintain tracking calls, move them into the framework they're using.`}</p>
    <p>{`For us it's Backbone. So last night I wrote a simple 30 line `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Mixin",
        "title": "Mixin"
      }}>{`mixin`}</a>{` for Backbone views that makes sure we track `}<em parentName="p">{`everything`}</em>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// copied straight from backbone
var delegateEventSplitter = /^(\\S+)\\s*(.*)$/;

// inspired by Backbone
Backbone.View.prototype.delegateEvents = _.wrap(
  Backbone.View.prototype.delegateEvents,
  function (fn, events) {
    if (!(events || (events = _.result(this, "events")))) return this;

    var track = _.bind(function (event) {
      var $target = $(event.currentTarget),
        event_name = $target.attr("data-mixpanel"),
        data = !!this.model ? this.model.toJSON() : {};

      if (event_name) {
        mixpanel.track(event_name, data);
      }
    }, this);

    for (var key in events) {
      var match = key.match(delegateEventSplitter),
        type = match[1],
        selector = match[2];

      if (type == "click" || type == "submit") {
        if (selector === "") {
          this.$el.on(type, track);
        } else {
          this.$el.on(type, selector, track);
        }
      }
    }

    return fn.apply(this);
  }
);
`}</code></pre>
    <p>{`The code wraps and is heavily inspired by `}<inlineCode parentName="p">{`Backbone.View.delegateEvents`}</inlineCode>{`, which is called when a new view is instantiated. Its job is to go through the events configuration hash and bind the view's event listeners to correct events on particular `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/HTML_element",
        "title": "HTML element"
      }}>{`HTML elements`}</a>{`.`}</p>
    <p>{`Nifty.`}</p>
    <p>{`My mixin does the same, but only cares about `}<inlineCode parentName="p">{`click`}</inlineCode>{` and `}<inlineCode parentName="p">{`submit`}</inlineCode>{` events since those represent most user actions. Every such event is bound to a simple `}<inlineCode parentName="p">{`track`}</inlineCode>{` function, which takes the `}<inlineCode parentName="p">{`data-mixpanel`}</inlineCode>{` property from the element as an event's business name, and uses the view's whole model as `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Metadata",
        "title": "Metadata"
      }}>{`meta data`}</a>{`.`}</p>
    <p>{`I figured the more data we can collect the better. You never know what you're going to need in the future.`}</p>
    <p>{`From now on, we just have to add a `}<inlineCode parentName="p">{`data-mixpanel`}</inlineCode>{` property to any element whose user actions we want to track. No property, no tracking. Simple.`}</p>
    <p>{`And yes, I know what you're thinking, `}<em parentName="p">{`surely`}</em>{` there's already a plugin for this. There is, it's called `}<a parentName="p" {...{
        "href": "https://github.com/bnorton/backbone-mixpanel"
      }}>{`backbone-mixpanel`}</a>{`. Looks like a pretty good solution, but for once I wanted to put my NotInventedHere blinds on and make something myself. :)`}</p>
    <p>{`Either way, this should remove the friction between us and good actionable metrics.`}</p>
    <p>{`PS: yes, before you get up in arms, I do need to add the code that cleans up events by wrapping the `}<inlineCode parentName="p">{`Backbone.View.undelegateEvents`}</inlineCode>{` method.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=238011f3-8be3-4b29-a1af-b7f9ac498401",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      